/**
 * Chat Message Component
 * 
 * Displays a single message in the chat with avatar and timestamp.
 */

import React from "react";
import { IChatMessage, TBadgeType } from "../types/venchy.types";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "../venchy.module.css";

interface IChatMessageProps {
  message: IChatMessage;
  userInitial: string;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}

const getBadgeClass = (badgeType: TBadgeType): string => {
  switch (badgeType) {
    case 'success': return styles.venchyBadgeSuccess;
    case 'warning': return styles.venchyBadgeWarning;
    case 'danger': return styles.venchyBadgeDanger;
    case 'info': return styles.venchyBadgeInfo;
    default: return '';
  }
};

const ChatMessage: React.FC<IChatMessageProps> = ({
  message,
  userInitial,
  onApprove,
  onReject
}) => {
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`${styles.venchyMessage} ${message.sender === 'user' ? styles.user : styles.assistant}`}>
      <div className={styles.venchyMessageAvatar}>
        {message.sender === 'user' ? userInitial : '🤖'}
      </div>
      <div className={styles.venchyMessageContent}>
        <div className={styles.venchyMessageBubble}>
          {message.content}
          
          {/* Action Card */}
          {message.actionCard && (
            <div className={styles.venchyActionCard}>
              <div className={styles.venchyActionCardHeader}>
                <div className={styles.venchyActionCardTitle}>
                  {message.actionCard.title}
                </div>
                <div className={`${styles.venchyActionCardBadge} ${getBadgeClass(message.actionCard.badgeType)}`}>
                  {t(message.actionCard.badge)}
                </div>
              </div>
              <div className={styles.venchyActionCardDetails}>
                {message.actionCard.details}
              </div>
              {message.actionCard.showButtons && (
                <div className={styles.venchyActionButtons}>
                  <button 
                    className={`${styles.venchyActionBtn} ${styles.approve}`}
                    onClick={() => onApprove?.(message.actionCard!.id)}
                  >
                    {t("VENCHY_BTN_APPROVE")}
                  </button>
                  <button 
                    className={`${styles.venchyActionBtn} ${styles.reject}`}
                    onClick={() => onReject?.(message.actionCard!.id)}
                  >
                    {t("VENCHY_BTN_REJECT")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.venchyMessageTime}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
